import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
// Per-function subpaths: every component imports this module, and the
// `date-fns` and `date-fns/locale` barrels load every function and every
// locale (over a thousand ESM files) in each test worker.
import { format } from 'date-fns/format';
import { fr } from 'date-fns/locale/fr';
import { currencyCode, currencyLabel, roundMoney, type CurrencyRef } from '@efacture/fiscal-core';
import { toNum, type DecimalString } from './decimal';

/**
 * Combines class names with Tailwind merge support
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format an amount followed by the symbol of `currency` (« FCFA » for the
 * franc CFA, the ISO code or the symbol a certification period states
 * otherwise); a currency no table nor definition words prints its code.
 * @returns e.g. "1 500 000 FCFA", "11,81 EUR"
 */
export function formatMoney(amount: number | DecimalString, currency: CurrencyRef, centimes = false): string {
  let symbol: string;
  try {
    symbol = currencyLabel(currency).symbol;
  } catch {
    symbol = currencyCode(currency);
  }
  return `${formatNumber(toNum(amount), centimes)} ${symbol}`;
}

/**
 * Format an amount without a currency symbol (French locale): whole amounts
 * without decimals unless `centimes`, other amounts with two (the centime)
 * @param amount - The amount to format
 * @returns Formatted number string (e.g., "1 500 000", "76,27", "0,00")
 */
export function formatNumber(amount: number, centimes = false): string {
  if (!Number.isFinite(amount)) return new Intl.NumberFormat('fr-FR').format(amount);
  const rounded = roundMoney(amount);
  const digits = centimes || !Number.isInteger(rounded) ? 2 : 0;
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rounded);
}

/**
 * Format a date in French locale
 * @param date - The date to format (Date object or ISO string)
 * @param formatString - The format pattern (default: 'dd/MM/yyyy')
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | null | undefined,
  formatString: string = 'dd/MM/yyyy'
): string {
  // date-fns throws RangeError on an invalid date, which unmounts the whole
  // page through the router error boundary. One absent field is worth a dash,
  // never a blank screen.
  if (date === null || date === undefined) return '—';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return '—';
  return format(dateObj, formatString, { locale: fr });
}

/**
 * Format a date with time in French locale
 * @param date - The date to format
 * @returns Formatted datetime string (e.g., "15/01/2024 14:30")
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  return formatDate(date, 'dd/MM/yyyy HH:mm');
}

/**
 * Format a percentage
 * @param value - The value to format (0.18 for 18%)
 * @returns Formatted percentage string (e.g., "18 %")
 */
export function formatPercent(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Truncate text with ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

/**
 * Period-filter defaults, as `yyyy-MM-dd` for an `<input type="date">`.
 *
 * Built from LOCAL calendar parts, never `toISOString()`. Local midnight is
 * the PREVIOUS day in UTC anywhere east of Greenwich, so `toISOString()`
 * renders the first of August as 31 July in Paris, Dubai or Auckland.
 */
function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today. */
export function todayInput(): string {
  return toDateInput(new Date());
}

/** First day of the current month — the unit a bank reconciliation works in. */
export function startOfMonthInput(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

/**
 * 1 January of `year`, defaulting to the current one. The fiscal exercise
 * is the calendar year (`exerciseBounds`, accounting-reports.service), so
 * this is also the first day of an exercise.
 */
export function startOfYearInput(year?: number): string {
  return toDateInput(new Date(year ?? new Date().getFullYear(), 0, 1));
}

/** 31 December of `year`, defaulting to the current one. */
export function endOfYearInput(year?: number): string {
  return toDateInput(new Date(year ?? new Date().getFullYear(), 11, 31));
}

/** N days before today. */
export function daysAgoInput(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
}

/** Monday of the current week. */
export function startOfWeekInput(): string {
  const now = new Date();
  const day = now.getDay();
  const back = day === 0 ? 6 : day - 1;
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), now.getDate() - back));
}

/** First day of the current quarter. */
export function startOfQuarterInput(): string {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1));
}
